import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Injectable, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { CustomerTier } from './mobile-session.service';
import { environment } from '../environments/environment';

export type MobileCategory = {
  id: string;
  code: string;
  nameDutch: string;
  nameEnglish: string;
  nameFrench: string;
};

export type TierPriceRange = {
  start: number;
  end: number;
  goldPriceCents: number;
  silverPriceCents: number;
  bronzePriceCents: number;
};

export type MobileProduct = {
  id: string;
  code: string;
  taxPercentage: number;
  packageSize: number;
  nameDutch: string;
  nameEnglish: string;
  nameFrench: string;
  descriptionDutch: string;
  descriptionEnglish: string;
  descriptionFrench: string;
  imageLinks: string[];
  videoLink: string;
  categories: MobileCategory[];
  priceRanges: TierPriceRange[];
};

type ApiResponse<T> = {
  success: boolean;
  data: T;
};

@Injectable({ providedIn: 'root' })
export class MobileProductsService {
  private readonly apiBaseURL = this.resolveAPIBaseURL(environment.apiBaseUrl);
  private readonly catalogApiURL = `${this.apiBaseURL}/api/v1/bg-imports/public/products-catalog`;
  private readonly legacyApiURL = `${this.apiBaseURL}/api/v1/bg-imports/public/products`;

  public readonly loading = signal<boolean>(false);
  public readonly error = signal<string>('');
  public readonly products = signal<MobileProduct[]>([]);

  constructor(private readonly http: HttpClient) {}

  public async load(): Promise<void> {
    if (this.loading()) {
      return;
    }

    this.loading.set(true);
    this.error.set('');

    try {
      const response = await this.fetchProductsWithFallback();
      const rows = Array.isArray(response?.data) ? response.data : [];
      this.products.set(rows.map((row) => this.mapProduct(row)));
    } catch {
      this.error.set('Failed to load products for dashboard.');
    } finally {
      this.loading.set(false);
    }
  }

  public tierPriceCents(range: TierPriceRange, tier: CustomerTier): number {
    if (tier === 'gold') {
      return range.goldPriceCents;
    }
    if (tier === 'silver') {
      return range.silverPriceCents;
    }
    return range.bronzePriceCents;
  }

  public centsToEuro(cents: number): string {
    return (Number(cents || 0) / 100).toFixed(2);
  }

  public normalizePackageSize(rawValue: number): number {
    const size = Math.floor(Number(rawValue) || 1);
    return size > 0 ? size : 1;
  }

  public normalizeQuantity(quantity: number, packageSize: number): number {
    const qty = Math.max(1, Math.floor(Number(quantity) || 1));
    const size = this.normalizePackageSize(packageSize);
    if (size === 1 || qty%size === 0) {
      return qty;
    }
    return Math.ceil(qty / size) * size;
  }

  private async fetchProductsWithFallback(): Promise<ApiResponse<any[]>> {
    try {
      return await this.fetchFromURL(this.catalogApiURL);
    } catch (error) {
      if (error instanceof HttpErrorResponse && error.status === 404) {
        return this.fetchFromURL(this.legacyApiURL);
      }
      throw error;
    }
  }

  private fetchFromURL(url: string): Promise<ApiResponse<any[]>> {
    const headers = new HttpHeaders({
      'x-request-id': this.newRequestID(),
    });
    return firstValueFrom(this.http.get<ApiResponse<any[]>>(url, { headers }));
  }

  private mapProduct(row: any): MobileProduct {
    const categoriesRaw = Array.isArray(row?.categories)
      ? row.categories
      : Array.isArray(row?.Categories)
        ? row.Categories
        : [];

    const categories = categoriesRaw.map((item: any) => ({
      id: String(item?.id ?? ''),
      code: String(item?.code ?? item?.Code ?? ''),
      nameDutch: String(item?.name_dutch ?? item?.NameDutch ?? ''),
      nameEnglish: String(item?.name_english ?? item?.NameEnglish ?? ''),
      nameFrench: String(item?.name_french ?? item?.NameFrench ?? ''),
    }));

    const ranges: TierPriceRange[] = [1, 2, 3, 4, 5]
      .map((index) => ({
        start: Number(row?.[`range_${index}_start`] ?? 0),
        end: Number(row?.[`range_${index}_end`] ?? 0),
        goldPriceCents: Number(row?.[`range_${index}_gold_price`] ?? 0),
        silverPriceCents: Number(row?.[`range_${index}_silver_price`] ?? 0),
        bronzePriceCents: Number(row?.[`range_${index}_bronze_price`] ?? 0),
      }))
      .filter((item) => item.start > 0 || item.end > 0 || item.goldPriceCents > 0 || item.silverPriceCents > 0 || item.bronzePriceCents > 0);

    const imageLinks = [
      row?.product_image_link_1,
      row?.product_image_link_2,
      row?.product_image_link_3,
      row?.product_image_link_4,
      row?.product_image_link_5,
    ]
      .map((value) => String(value ?? '').trim())
      .filter((value) => value !== '');

    return {
      id: String(row?.id ?? ''),
      code: String(row?.code ?? ''),
      taxPercentage: Number(row?.tax_percentage ?? row?.taxPercentage ?? row?.TaxPercentage ?? 0),
      packageSize: this.normalizePackageSize(Number(row?.package_size ?? row?.packageSize ?? row?.PackageSize ?? 1)),
      nameDutch: String(row?.name_dutch ?? ''),
      nameEnglish: String(row?.name_english ?? ''),
      nameFrench: String(row?.name_french ?? ''),
      descriptionDutch: String(row?.description_dutch ?? ''),
      descriptionEnglish: String(row?.description_english ?? ''),
      descriptionFrench: String(row?.description_french ?? ''),
      imageLinks,
      videoLink: String(row?.product_video_link_1 ?? ''),
      categories,
      priceRanges: ranges,
    };
  }

  private newRequestID(): string {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
    return `bgi-mobile-${Date.now()}-${Math.random().toString(16).slice(2)}`;
  }

  private resolveAPIBaseURL(url: string): string {
    return String(url || '').replace(/\/+$/, '');
  }
}
